import React, { useState } from 'react';
import { scanSavedDevices } from '../services/api';
import { Activity, Play, CheckCircle, XCircle, Clock, Server } from 'lucide-react';
import Skeleton from '../Components/SkeletonLoader';

const NetworkScan = () => {
    const [scanResults, setScanResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [lastScanTime, setLastScanTime] = useState(null);

    const handleScan = async () => {
        setLoading(true);
        setError(null);
        setScanResults([]); // Clear previous results
        try {
            const response = await scanSavedDevices();
            console.log("scanSavedDevices response:", response);
            setScanResults(Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []));
            setLastScanTime(new Date());
        } catch (err) {
            console.error("Scan failed:", err);
            setError("Network scan failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const filteredResults = scanResults.filter(result => {
        if (filterStatus === 'online') return result.isOnline;
        if (filterStatus === 'offline') return !result.isOnline;
        return true;
    });

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            {/* Header Section ... (keep same) ... */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Network Scan</h1>
                    <p className="text-sm text-gray-500 mt-1">Check real-time status of all saved devices.</p>
                </div>
                
                <div className="flex items-center gap-3">
                    {lastScanTime && (
                         <div className="hidden md:flex flex-col items-end mr-2">
                            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Last Scan</span>
                            <span className="text-sm font-mono font-medium text-gray-700">{lastScanTime.toLocaleTimeString()}</span>
                         </div>
                    )}
                    <button
                        onClick={handleScan}
                        disabled={loading}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border font-medium text-sm transition-all ${
                            loading 
                             ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                             : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50 hover:border-blue-300 shadow-sm'
                        }`}
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-200 border-t-blue-600"></div>
                                <span>Scanning Network...</span>
                            </>
                        ) : (
                            <>
                                <Play className="h-4 w-4" />
                                <span>Start Quick Scan</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    {error}
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && scanResults.length === 0 && !lastScanTime && (
                <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border border-gray-200 border-dashed">
                    <div className="p-4 rounded-full bg-blue-50 mb-4">
                         <Activity className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">No Scan Data</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm text-center">Start a new network scan to check connectivity and retrieve device status.</p>
                </div>
            )}

            {/* Loading Skeleton */}
             {loading && scanResults.length === 0 && (
                <div className="space-y-4">
                     {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-16 w-full rounded-xl" />
                     ))}
                </div>
            )}

            {/* Results Section */}
            {scanResults.length > 0 && (
                <div className="space-y-4">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                         <button 
                            onClick={() => setFilterStatus('all')}
                            className={`p-4 rounded-xl border shadow-sm text-left transition-all ${
                                filterStatus === 'all' 
                                    ? 'bg-white border-blue-500 ring-1 ring-blue-500' 
                                    : 'bg-white border-gray-100 hover:border-gray-200'
                            }`}
                         >
                             <p className="text-xs font-bold text-gray-500 uppercase">Total Scanned</p>
                             <p className="text-2xl font-bold text-gray-900 mt-1">{scanResults.length}</p>
                         </button>
                         <button 
                            onClick={() => setFilterStatus('online')}
                            className={`p-4 rounded-xl border shadow-sm text-left transition-all ${
                                filterStatus === 'online' 
                                    ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-500' 
                                    : 'bg-emerald-50 border-emerald-100 hover:border-emerald-200'
                            }`}
                         >
                             <p className="text-xs font-bold text-emerald-600 uppercase">Online</p>
                             <p className="text-2xl font-bold text-emerald-800 mt-1">
                                {scanResults.filter(r => r.isOnline).length}
                             </p>
                         </button>
                         <button 
                            onClick={() => setFilterStatus('offline')}
                            className={`p-4 rounded-xl border shadow-sm text-left transition-all ${
                                filterStatus === 'offline' 
                                    ? 'bg-rose-50 border-rose-500 ring-1 ring-rose-500' 
                                    : 'bg-rose-50 border-rose-100 hover:border-rose-200'
                            }`}
                         >
                             <p className="text-xs font-bold text-rose-600 uppercase">Offline</p>
                             <p className="text-2xl font-bold text-rose-800 mt-1">
                                {scanResults.filter(r => !r.isOnline).length}
                             </p>
                         </button>
                    </div>

                    {/* Results Table */}
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse table-fixed">
                                <thead>
                                    <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        <th className="px-6 py-4 w-[140px]">Status</th>
                                        <th className="px-6 py-4 w-[240px]">Device Info</th>
                                        <th className="px-6 py-4 w-[160px]">Response Time</th>
                                        <th className="px-6 py-4 w-[240px]">Last Seen</th>
                                        <th className="px-6 py-4 w-auto">System Message</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredResults.length > 0 ? (
                                        filteredResults.map((result, idx) => (
                                        <tr key={idx} className="group hover:bg-blue-50/30 transition-colors">
                                            <td className="px-6 py-3.5">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${result.isOnline
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-rose-50 text-rose-700 border-rose-200'
                                                    }`}>
                                                    <span className={`h-1.5 w-1.5 rounded-full ${result.isOnline ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                    {result.isOnline ? 'Online' : 'Offline'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3.5">
                                                 <div className="flex flex-col">
                                                     <div className="flex items-center gap-2 mb-0.5">
                                                         <Server className="h-3.5 w-3.5 text-gray-400" />
                                                         <span className="text-sm font-medium font-mono text-gray-900">{result.ipAddress}</span>
                                                     </div>
                                                     {result.deviceName && (
                                                         <span className="text-xs text-gray-500 font-medium pl-6 truncate max-w-[180px]" title={result.deviceName}>
                                                             {result.deviceName}
                                                         </span>
                                                     )}
                                                 </div>
                                            </td>
                                            <td className="px-6 py-3.5">
                                                <span className={`font-mono text-sm ${
                                                    result.isOnline ? 'text-gray-600' : 'text-gray-400'
                                                }`}>
                                                    {result.isOnline ? `${result.responseTime}ms` : '-'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3.5">
                                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                                                    {new Date(result.lastSeen.endsWith('Z') ? result.lastSeen.slice(0, -1) : result.lastSeen).toLocaleString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: true
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3.5 text-sm text-gray-500">
                                                {result.status}
                                            </td>
                                        </tr>
                                    ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <CheckCircle className="h-6 w-6 text-gray-300" />
                                                    <span className="text-sm font-medium">No results found for {filterStatus} devices</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NetworkScan;
